import { useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useToastContext } from "components/alert/ToastContext";
import NotaFiscalSaida from "model/vendas/NotaFiscalSaida";
import NotaFiscalSaidaEmail from "model/vendas/NotaFiscalSaidaEmail";
import NotaFiscalSaidaService from "service/vendas/NotaFiscalSaidaService";

const service = new NotaFiscalSaidaService();
const situacoes: Record<NotaFiscalSaidaEmail["status"], string> = {
    PENDENTE: "Pendente", PROCESSANDO: "Processando", ENVIADO: "Enviado", FALHOU: "Falhou", INCERTO: "Envio incerto"
};
const data = (valor: string | null) => valor ? new Date(valor).toLocaleString("pt-BR") : "—";

export default function NotaFiscalSaidaEmails({ nota, podeGerenciar }: { nota: NotaFiscalSaida; podeGerenciar: boolean }) {
    const [envios, setEnvios] = useState<NotaFiscalSaidaEmail[]>([]);
    const [destinatario, setDestinatario] = useState(nota.emailDestinatario ?? "");
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState(false);
    const [revisao, setRevisao] = useState(0);
    const { msgErro, msgSucesso } = useToastContext();
    const pendente = envios.some(envio => ["PENDENTE", "PROCESSANDO"].includes(envio.status));
    const autorizada = ["AUTORIZADA", "AUTORIZADA_ESTOQUE_PENDENTE"].includes(nota.status)
        && !!nota.protocoloAutorizacao && !nota.arquivada && !nota.statusProcessamento?.startsWith("CANCELAMENTO");

    useEffect(() => {
        setDestinatario(nota.emailDestinatario ?? "");
    }, [nota.id, nota.emailDestinatario]);

    useEffect(() => {
        let ativo = true;
        setCarregando(true);
        setErro(false);
        service.listarEmails(nota.id)
            .then(lista => { if (ativo) setEnvios(lista); })
            .catch(() => { if (ativo) setErro(true); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [nota.id, revisao]);

    useEffect(() => {
        if (!pendente || erro || carregando) return;
        const timer = window.setTimeout(() => setRevisao(valor => valor + 1), 5000);
        return () => window.clearTimeout(timer);
    }, [pendente, erro, carregando]);

    async function enviar() {
        if (enviando || pendente || !autorizada || !podeGerenciar) return;
        setEnviando(true);
        try {
            const envio = await service.enviarEmail(nota.id, destinatario.trim());
            setEnvios(lista => [envio, ...lista]);
            msgSucesso("E-mail da nota fiscal", "Envio agendado. Acompanhe o resultado no histórico.");
        } catch (error) {
            const detalhe = isAxiosError<{ detail?: string; message?: string }>(error)
                ? error.response?.data?.detail || error.response?.data?.message : null;
            msgErro("E-mail da nota fiscal", detalhe || "Não foi possível agendar o envio. Atualize o histórico antes de tentar novamente.");
            setRevisao(valor => valor + 1);
        } finally {
            setEnviando(false);
        }
    }

    function solicitarEnvio() {
        if (envios.some(envio => ["ENVIADO", "INCERTO"].includes(envio.status))) {
            confirmDialog({
                header: "Reenviar nota fiscal", icon: "pi pi-envelope",
                message: `Esta nota possui um envio confirmado ou incerto. Enviar novamente o XML e o DANFE para ${destinatario.trim()}?`,
                acceptLabel: "Reenviar", rejectLabel: "Voltar", accept: () => { void enviar(); }
            });
        } else {
            void enviar();
        }
    }

    return <div>
        <p>O XML autorizado e o DANFE em PDF são enviados automaticamente após a autorização da nota.</p>
        <p>“Enviado” indica aceitação pelo servidor de e-mail. Envios incertos exigem verificar o recebimento antes de reenviar.</p>
        {podeGerenciar && <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
            <div className="d-flex flex-column gap-2">
                <label htmlFor={`email-nota-${nota.id}`}>E-mails do comprador</label>
                <InputText id={`email-nota-${nota.id}`} type="text" inputMode="email" value={destinatario} maxLength={2048}
                    aria-describedby={`email-nota-ajuda-${nota.id}`} placeholder="comprador@empresa.com.br;fiscal@empresa.com.br"
                    style={{ width: "min(350px, 100%)" }} disabled={enviando || pendente || !autorizada}
                    onChange={event => setDestinatario(event.target.value)} />
                <small id={`email-nota-ajuda-${nota.id}`}>Separe os e-mails com ponto e vírgula (;).</small>
            </div>
            <Button label={envios.length ? "Reenviar e-mail" : "Enviar e-mail"} icon="pi pi-envelope" loading={enviando}
                disabled={carregando || erro || enviando || pendente || !autorizada || !destinatario.trim()}
                onClick={solicitarEnvio} />
        </div>}
        {!autorizada && <p>O envio está disponível somente para notas autorizadas, sem cancelamento pendente.</p>}
        <Button label="Atualizar histórico" icon="pi pi-refresh" outlined disabled={carregando || enviando}
            className="mb-3" onClick={() => setRevisao(valor => valor + 1)} />
        {erro ? <p role="alert">Não foi possível carregar o histórico de e-mails. Tente novamente.</p> :
            <DataTable value={envios} loading={carregando} emptyMessage="Nenhum envio registrado para esta nota" responsiveLayout="scroll">
                <Column header="Solicitado em" body={(envio: NotaFiscalSaidaEmail) => data(envio.dataSolicitacao)} />
                <Column header="Origem" body={(envio: NotaFiscalSaidaEmail) => envio.automatico ? "Automático" : "Manual"} />
                <Column field="destinatario" header="Destinatários solicitados" />
                <Column field="destinatarioEfetivo" header="Destinatários efetivos" />
                <Column header="Situação" body={(envio: NotaFiscalSaidaEmail) => <Tag value={situacoes[envio.status]}
                    severity={envio.status === "ENVIADO" ? "success" : envio.status === "FALHOU" ? "danger" : envio.status === "INCERTO" ? "warning" : "info"} />} />
                <Column header="Tentativa em" body={(envio: NotaFiscalSaidaEmail) => data(envio.dataTentativa)} />
                <Column header="Enviado em" body={(envio: NotaFiscalSaidaEmail) => data(envio.dataEnvio)} />
                <Column field="erro" header="Detalhes" />
            </DataTable>}
    </div>;
}
